import {AfterViewInit, Component, forwardRef, Inject, ViewChild} from '@angular/core';
import {KioskService} from '../../services/kiosk.service';
import {TableComponent} from '../../components/table.component';
import {AppComponent} from '../../app.component';
import {Column} from '../../common/column';
import {ActivatedRoute, Router} from '@angular/router';
import {Location} from '@angular/common';
import {Helper} from '../../common/helper';
import {KioskInfoBean, KioskState, KioskTemplateBean} from '../../model/model';
import {MapModalComponent} from '../../components/map.modal.component';
import {KiosksQuickviewComponent} from './kiosks.quickview.component';
import {RightsGuard} from '../../common/auth.guard';
import {Rights} from '../../common/rights';
import {StoreManagerService} from '../../services/store-manager.service';

@Component({
    templateUrl: 'kiosks.component.html',
})

export class KiosksComponent implements AfterViewInit {

    @ViewChild(TableComponent, {static: false}) table: TableComponent;
    @ViewChild('kiosksQuickviewComponent', {static: false}) kiosksQuickviewComponent: KiosksQuickviewComponent;
    @ViewChild(MapModalComponent, {static: false}) mapModalComponent: MapModalComponent;

    columns: Column[] = [
        new Column('name', 'Name').button(this, this.editKiosk),
        new Column('production', 'Production').checkbox(),
        new Column('stateCopy', 'State'),
        new Column('operationalSince', 'Operational Since').date(),
    ];

    templates: KioskTemplateBean[];
    compareBeans = Helper.compareBeans;

    averageLatitude = 0;
    averageLongitude = 0;
    kiosks: KioskInfoBean[];
    selectedStates: KioskState[] = [];
    defaultStates: KioskState[] = [
        // 'OUT_OF_SERVICE',
        'WAITING',
        'OPERATIONAL',
        'MAINTENANCE',
        'PROVISIONING',
    ];
    filteredKiosk: KioskInfoBean[] = [];
    selectedMarkerKiosk: KioskInfoBean;

    constructor(private kioskService: KioskService,
        private storeManagerService: StoreManagerService,
        private route: ActivatedRoute,
        private router: Router,
        private location: Location,
        @Inject(forwardRef(() => AppComponent)) private app: AppComponent) {
    }

    ngAfterViewInit(): void {

        this.route.queryParams.subscribe(params => {

            if (params['states']) {
                for (const param of params['states'].split(',')) {
                    this.selectedStates.push(param);
                }
            } else {
                this.selectedStates = this.defaultStates;
            }

            Helper.selectPicker('select_state');
            this.loadKiosks()
        });
    }

    loadKiosks() {

        if (RightsGuard.isAdmin() || RightsGuard.isSuperAdmin() || RightsGuard.isDeveloper()) {

            this.kioskService.getKiosksInfo().subscribe(kiosks => {
                this.setKiosks(kiosks.value);
            });
        } else {

            this.storeManagerService.getKiosksInfo().subscribe(kiosks => {
                this.setKiosks(kiosks.value);
            });
        }
    }

    setKiosks(kiosks) {
        this.kiosks = kiosks;
        this.kiosks.sort((a, b) => a.name.localeCompare(b.name));

        let maxLat = -Number.MAX_SAFE_INTEGER;
        let maxLng = -Number.MAX_SAFE_INTEGER;
        let minLat = Number.MAX_SAFE_INTEGER;
        let minLng = Number.MAX_SAFE_INTEGER;

        this.filteredKiosk = [];
        for (const kiosk of kiosks) {

            if (kiosk.latitude != 0 && kiosk.longitude != 0) {
                if (kiosk.latitude > maxLat) {
                    maxLat = kiosk.latitude;
                }
                if (kiosk.longitude > maxLng) {
                    maxLng = kiosk.longitude;
                }
                if (kiosk.latitude < minLat) {
                    minLat = kiosk.latitude;
                }
                if (kiosk.longitude < minLng) {
                    minLng = kiosk.longitude;
                }
            }

            const kioskCopy = Helper.copyObject(kiosk);
            kioskCopy['stateCopy'] = kiosk.state;

            for (const state of this.selectedStates) {
                if (state == kioskCopy['stateCopy']) {
                    this.filteredKiosk.push(kioskCopy);
                }
            }
        }

        this.averageLongitude = minLng + ((maxLng - minLng) / 2);
        this.averageLatitude = minLat + ((maxLat - minLat) / 2);
        this.table.setData(this.filteredKiosk, this.columns);
    }

    onStateChange() {
        this.setKiosks(this.kiosks);
        // this.location.replaceState('/kiosks?location=' + this.selectedOperator.id + '&states=' + this.selectedStates)
        this.location.replaceState('/kiosks?states=' + this.selectedStates);
    }

    onView(items: KioskInfoBean[]) {

        if (items && items.length > 0) {
            this.router.navigate(['/kiosk-view/'], {
                queryParams: {
                    kioskId: items[0].kioskId,
                    operatorId: items[0].operatorId
                }
            });
        }
    }

    onStats(items: KioskInfoBean[]) {

        if (items && items.length > 0) {
            this.router.navigate(['/kiosk-information/'], {
                queryParams: {
                    kioskId: items[0].kioskId,
                    operatorId: items[0].operatorId
                }
            });
        }
    }

    onEdit(items: KioskInfoBean[]) {
        if (items && items.length > 0) {
            this.editKiosk(items[0]);
        }
    }

    editKiosk(item: KioskInfoBean) {
        if (RightsGuard.userAccess([Rights.MANAGE_KIOSKS]) || RightsGuard.userAccess([Rights.MANAGE_ALL_STORES])) {

            this.router.navigate(['/kiosk-details/'], {
                queryParams: {
                    kioskId: item ? item.kioskId : 0,
                    operatorId: item ? item.operatorId : 0
                }
            }).then(success => {
                if (!success) {
                    this.app.showError('Permission denied');
                }
            });
        } else {
            this.app.showError('Permission denied');
        }
    }

    onAdd() {
        this.editKiosk(null)
    }

    onRemove(items) {
        for (const kiosk of items) {

            this.kioskService.deleteKiosk(kiosk).subscribe(response => {
                if (response.success) {
                    this.loadKiosks();

                } else {
                    this.app.showError('Can\'t delete kiosk');
                }
            }, error => {
                this.app.showError('Can\'t delete kiosk');
            });
        }
    }

    openMap(item: KioskInfoBean) {
        this.mapModalComponent.open(item.latitude, item.longitude);
    }

    clickedMarker(item: KioskInfoBean) {
        this.selectedMarkerKiosk = item;
    }

    onClickRow(item: KioskInfoBean) {
        this.kiosksQuickviewComponent.open(item.kioskId, item.operatorId, item.name);
    }

    onQuickView(items: KioskInfoBean[]) {
        if (items && items.length > 0) {
            this.router.navigate(['/store-manager-kiosk-view/'], {
                queryParams: {
                    kioskId: items[0].kioskId,
                    operatorId: items[0].operatorId
                }
            });
        }
    }

    canEditKiosk() {
        return RightsGuard.userAccess([Rights.MANAGE_KIOSKS]);
    }
}
